import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatButtonModule } from '@angular/material/button';
import { MatTabChangeEvent, MatTabGroup, MatTabsModule } from '@angular/material/tabs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { BehaviorSubject } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { CountryCode } from 'libphonenumber-js';
import { OrganizationService } from '../services/organizations.service';
import { OrganizationColumns, OrganizationRow, getPendingOrganizationRowFromOrganization } from '../models/Organization';
import { SearchService } from '../../../shared/search/search.service';
import { DataTableFilter } from '../../../shared/components/data-table/filters/components/DataTableFilter';
import { OrganizationsFilterStatusComponent } from '../components/organizations-filter-status-component/organizations-filter-status.component';
import { ColorProp, getColor } from '../../../layout/Colors';
import { FilterParams } from '../../../shared/search/SearchParams';
import { Logger } from '../../../infrastructure/logging/logger';
import { NewPendingOrgTableComponent } from "./new-pending-org-table/new-pending-org-table.component";
import { DataTableFilterComponent } from '../../../shared/components/data-table/filters/components/data-table-filter/data-table-filter.component';
import { PillComponent } from '../../../shared/components/pill/pill.component';
import { UserContextProvider } from '../../../infrastructure/context/user-context-provider';
import { EmbeddedOrgTableComponent } from "./embedded-org-table/embedded-org-table.component";

@Component({
  selector: 'app-organizations',
  templateUrl: './organizations.component.html',
  styleUrls: ['./organizations.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIcon,
    MatButtonModule,
    MatTabsModule,
    MatTableModule,
    MatFormFieldModule,
    MatSortModule,
    MatPaginatorModule,
    MatInputModule,
    FormsModule,
    MatProgressSpinner,
    NewPendingOrgTableComponent,
    DataTableFilterComponent,
    PillComponent,
    EmbeddedOrgTableComponent
]
})
export class OrganizationsComponent implements OnInit, AfterViewInit {

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort: MatSort = new MatSort;
  @ViewChild(MatTabGroup) matTabGroup!: MatTabGroup;

  STATUS_ACTIVE = 1;
  STATUS_INACTIVE = 2;

  filterDefinitions: DataTableFilter[] = [
    { id: uuid(), name: 'Organization Status', shortName: 'Status', component: OrganizationsFilterStatusComponent }
  ];

  tableToApiColumnMap: { [key: string]: string } = {
    'id': 'organizationId',
    'name': 'name',
    'contactName': 'contactName',
    'email': 'email',
    'phoneNumber': 'phoneNumber',
    'pillStatus': 'status'
  }

  loading = new BehaviorSubject<boolean>(false);
  organization: any;
  loggedInUserRole!: string;
  displayedColumns = OrganizationColumns;
  displayedColumnNames: string[] = [];
  keyword!: string;
  pageNumber: number = 1;
  tableLength = 0;
  pageSize = 10;
  activeTab: number = 0;
  activeFilter: boolean = false;
  inactiveFilter: boolean = false;

  activeOrganizationsDataSource = new MatTableDataSource<OrganizationRow>();
  mappedActiveOrganizations: any[] = [];
  activeOrganizationsTableLength = 0;
  activeOrganizationsPageSize = 10;
  activeOrganizations: any[] = [];

  initialKeyword = new BehaviorSubject<string>('');
  initialSort = new BehaviorSubject<Sort | undefined>(undefined);
  initialPageSize = new BehaviorSubject<number>(10);
  initialPageNumber = new BehaviorSubject<number>(1);

  constructor(
    private router: Router,
    private organizationService: OrganizationService,
    private searchService: SearchService,
    private logger: Logger,
    private userContextProvider: UserContextProvider,
    private matSnackbar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.searchService.resetQuery();
    this.initTableColumns();
    this.loadPage();

    const storedItemsPerPage = localStorage.getItem('organizationItemsPerPage');
    if (storedItemsPerPage) {
      this.initialPageSize.next(parseInt(storedItemsPerPage, 10));
    } else {
      this.initialPageSize.next(10);
    }
  }

  ngAfterViewInit() {
    this.activeOrganizationsDataSource.paginator = this.paginator;
    this.activeOrganizationsDataSource.sort = this.sort;

    this.initialPageSize?.subscribe(pageSize => {
      this.pageSize = pageSize;
      this.paginator.pageSize = pageSize;
      this.cdr.detectChanges();
    });

    this.initialPageNumber?.subscribe(pageNumber => {
      if (pageNumber != 1) {
        this.pageNumber = pageNumber;
        this.paginator.pageIndex = pageNumber;
      } else {
        this.pageNumber = 1;
      }
      this.cdr.detectChanges();
    });

    this.initialKeyword?.subscribe(keyword => {
      this.keyword = keyword;
      this.cdr.detectChanges();
    });

    this.initialSort?.subscribe(sort => {
      if (!sort) {
        return;
      }

      this.setSort(sort);
      this.cdr.detectChanges();
    });
  }

  setSort(sort: Sort) {
    this.sort.sort({ id: sort.active, start: sort.direction, disableClear: false });
  }

  initTableColumns(): void {
    if (this.displayedColumns === null) {
      this.displayedColumns = [];
    }
    this.displayedColumnNames = this.displayedColumns.map(c => c.column);
  }

  async loadPage() {
    await this.loadActiveOrganizations();
  }

  async loadActiveOrganizations() {
    try {
      this.loading.next(true);
      this.activeOrganizationsDataSource.data = [];

      const organizations = await this.organizationService.getOrganizations();
      const activeOrgs = organizations.filter(org => org.status === "Active" || org.status === "Inactive");
      this.mappedActiveOrganizations = activeOrgs.map((org: any) => getPendingOrganizationRowFromOrganization(org, this.userContextProvider.countryCode as CountryCode));
      this.updateActiveTableDataSource(this.mappedActiveOrganizations);
    } catch(e) {
      this.logger.handleError(e, 'An error occurred while retrieving Organizations', 'Error while retrieving Organizations');
    } finally {
      this.loading.next(false);
    }
  }

  onFilterActionClick(event: any) {
    this.handleActiveOrgsStatusFilterUpdate(event.value);
  }

  setStatusSearchService(status: string) {
    const filter: FilterParams = {
      fieldName: 'status',
      value: [status],
      range: false
    };
    this.searchService.replaceFilter(filter);
  }

  updateActiveTableDataSource(activeInactiveOrganizations: OrganizationRow[]) {
    this.activeOrganizationsDataSource.data = activeInactiveOrganizations;
    this.activeOrganizationsTableLength = activeInactiveOrganizations.length;
  }

  onActiveOrganizationsPageEvent(event: PageEvent): void {
    if (this.activeFilter && !this.inactiveFilter) {
      this.setStatusSearchService("Active");
    }
    if (!this.activeFilter && this.inactiveFilter) {
      this.setStatusSearchService("Inactive");
    }

    this.activeOrganizationsPageSize = event.pageSize;
    this.searchService.setPageSize(event.pageSize);
    this.searchService.setPage(event.pageIndex + 1);
    this.loadActiveOrganizations();
  }

  private async handleActiveOrgsStatusFilterUpdate(status: string) {
    switch(status) {
      case "Active":
        this.updateActiveTableDataSource(this.mappedActiveOrganizations.filter(org => org.statusId === 1));
        break;
      case "Inactive":
        this.updateActiveTableDataSource(this.mappedActiveOrganizations.filter(org => org.statusId === 2));
        break;
      default:
        this.activeFilter = false;
        this.inactiveFilter = false;
        this.loadPage();
        break;
    }
  }

  onCreateOrganizationClick(): void {
    this.router.navigate(['/organizations/form']);
  }

  getOrganizationPillStatusColor(status: string): string {
    switch(status) {
      case "Active":
        return getColor(ColorProp.PropertyStatusActive);
      case "Inactive":
        return '#d3d3d3';
      case "Pending":
        return getColor(ColorProp.ReservationStatusClaimFiled);
      case "New":
        return getColor(ColorProp.GuestProcessing);
      default:
        return '#d3d3d3';
    }
  }

  onPageEvent(event: PageEvent): void {
    this.pageNumber = event.pageIndex + 1;
    localStorage.setItem('organizationItemsPerPage', event.pageSize.toString());
  }

  applySearch(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.activeOrganizationsDataSource.filter = filterValue.trim().toLowerCase();

    if (this.activeOrganizationsDataSource.paginator) {
      this.activeOrganizationsDataSource.paginator.firstPage();
    }
  }

  clearSearch() {
    this.keyword = '';
    this.activeOrganizationsDataSource = new MatTableDataSource(this.mappedActiveOrganizations);
    this.ngAfterViewInit();
  }

  onGoToPageClick() {
    const max = Math.trunc(Math.ceil(this.paginator.length / this.paginator.pageSize));
    if (this.pageNumber < 1) {
      this.resetPaginator(1, true);
      this.displayInvalidPaginatorPageIndexError();
    } else if (this.pageNumber > max) {
      this.resetPaginator(max, false);
      this.displayInvalidPaginatorPageIndexError();
    } else {
      this.paginator.pageIndex = this.pageNumber - 1;
      this.paginator.page.next({
        pageIndex: this.pageNumber - 1,
        pageSize: this.paginator.pageSize,
        length: this.paginator.length
      });
    }
  }

  resetPaginator(pageNumber: number, goToFirstPage: boolean) {
    this.pageNumber = pageNumber;

    if (this.paginator) {
      if (goToFirstPage) {
        this.paginator.firstPage();
      } else {
        this.paginator.lastPage();
      }
    }
  }

  displayInvalidPaginatorPageIndexError() {
    this.matSnackbar.open('Invalid page number', 'OK', { duration: 3000 });
  }

  onRowClick(row: any): void {
    this.router.navigate([`/organizations/detail/${row['id']}`]);
  }

  onTabChange(tabChangeEvent: MatTabChangeEvent) {
    this.organizationService.activeTabIndex = tabChangeEvent.index;
    this.router.navigate([], { relativeTo: this.route });
  }
}